import { useEffect, useMemo } from 'react';

import { yupResolver } from '@hookform/resolvers/yup/dist/yup';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    type DialogProps,
    DialogTitle,
    Typography,
    styled
} from '@mui/material';
import { endOfDay, startOfToday, subDays } from 'date-fns';
import { useSnackbar } from 'notistack';
import { unparse } from 'papaparse';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useContainer } from 'unstated-next';
import * as yup from 'yup';

import { normalizeChatRecord } from './utils';
import { ControlledDateRange } from '../../../../../components/forms';
import { StyledLoadingButton } from '../../../../../components/forms/LoadingButton';
import { Status } from '../../../../../types/apiTypes';
import DownloadChatContainer from '../../../DownloadChatContainer';
import { type ChatHistoricalRecord, type DownloadChatOptions } from '../types';

const StyledDialog = styled(Dialog)`
    & .MuiDialog-paper {
        width: 500px;
    }

    & .MuiDialogTitle-root + .MuiDialogContent-root {
        padding-top: 16px;
    }
` as typeof Dialog;

const DateRangeContainer = styled(Box)`
    padding: 0 1.5rem 0.5rem 1.5rem;

    & > div {
        display: flex;
        align-items: center;
    }
`;

type DownloadChatModalProps = DialogProps & {
    classId?: number | null;
    className?: string | null;
    userUuid: string | null;
    classUuid?: string | null;
};

export const DownloadChatModal = (props: DownloadChatModalProps) => {
    const { classId, className, userUuid, classUuid, onClose, ...other } = props;

    const { t } = useTranslation('admin_settings');

    const { getChats, getChatsStatus } = useContainer(DownloadChatContainer);

    const { enqueueSnackbar } = useSnackbar();

    const chatStatus = useMemo(() => getChatsStatus[classId ?? 0], [classId, getChatsStatus]);

    const schema = yup.object({
        downloadChatDateRange: yup.string().required(t('errors.required-date-range'))
    });

    const { control, getValues, handleSubmit, setError, watch, clearErrors } = useForm({
        resolver: yupResolver(schema)
    });

    const watchedDateRange = watch('downloadChatDateRange');

    useEffect(() => {
        if (watchedDateRange) {
            clearErrors('downloadChatDateRange');
        }
    }, [watchedDateRange, clearErrors]);

    const isMoment = (obj) => '_isAMomentObject' in obj;
    const momentToDate = (date) => (isMoment(date) ? date.toDate() : date);
    const dateToUnixTimestamp = (date: Date) => Math.floor(date.getTime() / 1000);

    const prepareDataForCsv = (chats: ChatHistoricalRecord[]) => {
        const headerRow = `${t('classes.sent-at')}, ${t('classes.to')}, ${t('classes.from')}, ${t('classes.text')}, ${t(
            'classes.hidden'
        )}\n`;

        const dataForCSV = chats.map((record) => {
            const { sentAt, to, from, text, hidden } = normalizeChatRecord(record, t);
            return { sentAt, to, from, text, hidden };
        });

        const parsedData = unparse(dataForCSV, {
            header: false,
            columns: ['sentAt', 'to', 'from', 'text', 'hidden']
        });

        return `${headerRow}${parsedData}`;
    };

    const downloadCsv = (chats: ChatHistoricalRecord[]) => {
        const data = prepareDataForCsv(chats);
        const blob = new Blob([new Uint8Array([0xef, 0xbb, 0xbf]), data], { type: 'text/csv;charset=utf-8;' });

        // creates a temporary link to download the file
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = t('classes.csv-title', { classTitle: className });

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handleClose = () => {
        onClose?.({}, 'backdropClick');
    };

    const onSubmit = async () => {
        const [startDate, endDate] = getValues('downloadChatDateRange').map(momentToDate);

        const selectedStartTime = dateToUnixTimestamp(startDate);
        const selectedEndTime = dateToUnixTimestamp(endOfDay(endDate));

        const params: DownloadChatOptions = {
            class_id: classId ?? 0,
            start_time: selectedStartTime,
            end_time: selectedEndTime,
            user_uuid: userUuid
        };

        if (classUuid) {
            params.class_uuid = classUuid;
        }

        const chats = await getChats(params);

        if (!chats) return;

        if (chats.length === 0) {
            setError('downloadChatDateRange', { message: t('classes.no-chats') });

            return;
        }

        downloadCsv(chats);

        enqueueSnackbar(t('classes.chat-download-confirmation'), { variant: 'success' });
        handleClose();
    };

    const isLoading = chatStatus === Status.InProgress;

    return (
        <StyledDialog onClose={handleClose} {...other}>
            <form onSubmit={handleSubmit(onSubmit)}>
                <DialogTitle>
                    <Typography variant="h5">
                        {t('classes.download-chat-for', {
                            classTitle: className,
                            interpolation: { escapeValue: false }
                        })}
                    </Typography>
                </DialogTitle>

                <DialogContent>{t('classes.download-chat-description')}</DialogContent>

                <DateRangeContainer>
                    <ControlledDateRange
                        name="downloadChatDateRange"
                        startLabel={t('classes.start-date')}
                        endLabel={t('classes.end-date')}
                        control={control}
                        separator={t('to')}
                        calendars={1}
                        defaultValue={[subDays(startOfToday(), 6), new Date()]}
                    />
                </DateRangeContainer>

                <DialogActions>
                    <Button onClick={handleClose} variant="outlined">
                        {t('cancel')}
                    </Button>

                    <StyledLoadingButton
                        id="chat-csv-download-btn"
                        disabled={!classId}
                        loading={isLoading}
                        variant="contained"
                        onClick={onSubmit}
                    >
                        {t('classes.export')}
                    </StyledLoadingButton>
                </DialogActions>
            </form>
        </StyledDialog>
    );
};
